/* eslint-disable react-refresh/only-export-components -- context + hook co-located per React convention */
import {
  createContext,
  useContext,
  useMemo,
  type JSX,
  type ReactNode,
} from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PracticeMembership } from '../api/me.ts';
import { resolveActivePractice } from './resolve-active-practice.ts';
import { useMe } from './use-me.ts';

/**
 * WHICH PRACTICE IS THIS SCREEN ACTING ON? - one rule, one place.
 *
 * Exactly two answers, and a screen is one kind or the other:
 *
 *   - a CASE-scoped screen takes the practice from the case row
 *     (`getCase(...).case.practice_id`). Never from here: a case belongs to one
 *     practice, so another practice's content on it is a control the server
 *     refuses.
 *   - a screen whose route names NO practice falls back to the DEFAULT membership. There
 *     is no chooser: anything that ACTS on a practice names it in the URL.
 *
 * `/me` orders memberships by practice NAME, so a screen picking one for itself
 * silently acts on the alphabetically-first practice of anyone in two.
 * tests/architecture/active-practice-binding.test.ts forbids that everywhere but
 * `resolveActivePractice`.
 *
 * THIS GRANTS NOTHING. It is a scope preference among practices the member
 * already belongs to, never an authorization input, and it never travels in a
 * token - SPEC §3 "Practice context" locks that. Every practice-scoped route
 * still takes an explicit `:practiceId` and re-checks membership server-side.
 *
 */

export interface ActivePractice {
  /** The membership every practice-scoped screen should act on. */
  readonly membership: PracticeMembership | null;
  readonly practiceId: string | null;
  /** Named on the screen so the practice being acted on is never invisible. */
  readonly practiceName: string | null;
  /** Every practice the member belongs to. */
  readonly memberships: readonly PracticeMembership[];
  /** True until `/me` resolves; `membership` is null and not yet meaningful. */
  readonly isLoading: boolean;
}

const ActivePracticeContext = createContext<ActivePractice | null>(null);

export function ActivePracticeProvider({ children }: { children: ReactNode }): JSX.Element {
  const me = useMe();
  const meData = me.data;
  const meIsLoading = me.isLoading;
  const value = useMemo<ActivePractice>(() => {
    const memberships = meData?.memberships ?? [];
    // No chosen id: nothing can choose one any more, so this is the DEFAULT membership.
    const membership = resolveActivePractice(memberships, null);
    return {
      membership,
      practiceId: membership?.practiceId ?? null,
      practiceName: membership?.practiceName ?? null,
      memberships,
      isLoading: meIsLoading,
    };
  }, [meData, meIsLoading]);

  return <ActivePracticeContext.Provider value={value}>{children}</ActivePracticeContext.Provider>;
}

/** Does the ACTIVE membership hold `capability`? usePersona's predicates answer over ANY
 *  membership, so a screen gated there and acting here offers what the server refuses. */
export function useActiveCapability(capability: string): boolean {
  return useActivePractice().membership?.capabilities.includes(capability) ?? false;
}

/**
 * The practice a screen should ACT ON: the one the URL names, or the active one when the
 * URL names none.
 *
 * ONE HOME. Slice D put the business screens behind `/practices/:practiceId/...` while the
 * unscoped paths stay reachable during the migration, so every such screen has to answer
 * "route or active?". The enquiry wizard answered it inline first, and the answer has to be
 * the same everywhere: a screen that reads the ACTIVE practice while the URL names another
 * acts on a different practice than the one the person clicked, which is the whole defect
 * class slice D exists to close.
 *
 * A route naming a practice the actor has NO membership in yields a null membership, never
 * a silent fall back to the active one — landing somewhere other than where the URL says is
 * worse than refusing.
 */
export function useScopedPractice(): {
  readonly membership: PracticeMembership | null;
  readonly practiceId: string | null;
  readonly practiceName: string | null;
  readonly isLoading: boolean;
  /** True when the URL named the practice, so the screen can say so instead of guessing. */
  readonly fromRoute: boolean;
} {
  const active = useActivePractice();
  const { practiceId: routePracticeId } = useParams<{ practiceId?: string }>();
  if (routePracticeId === undefined) {
    return {
      membership: active.membership,
      practiceId: active.practiceId,
      practiceName: active.practiceName,
      isLoading: active.isLoading,
      fromRoute: false,
    };
  }
  const membership = active.memberships.find((m) => m.practiceId === routePracticeId) ?? null;
  return {
    membership,
    practiceId: membership?.practiceId ?? null,
    practiceName: membership?.practiceName ?? null,
    isLoading: active.isLoading,
    fromRoute: true,
  };
}

/**
 * Does the SCOPED membership hold `capability`? The counterpart to useActiveCapability,
 * and the one a route-scoped screen must use: gating on the active practice while acting
 * on the route practice checks a different practice than the one being changed.
 */
/**
 * The practice a CASE route acts on. `useScopedPractice` reads `:practiceId`, and the
 * `/cases/:caseId/*` routes name none — so chrome fell back to the default membership and a
 * two-practice member read the wrong practice and role while working in a case. Reads the
 * case cache; `enabled: false` never fetches.
 */
export function useCasePracticeId(): string | null {
  const wild = useMatch('/cases/:caseId/*');
  const exact = useMatch('/cases/:caseId');
  const caseId = (wild ?? exact)?.params.caseId ?? null;
  // BOTH caches, read in a fixed order. An owner or admin with no case row — the §8.2
  // audience, and the reason this exists — is refused `getCase`, so ['case'] is never
  // filled for exactly the actor who most needs the right practice named.
  const fromCase = useQuery({ queryKey: ['case', caseId], enabled: false });
  const fromBusiness = useQuery({ queryKey: ['case-business', caseId], enabled: false });
  const practiceIdIn = (data: unknown): string | null =>
    (data as { case?: { practice_id?: string } } | undefined)?.case?.practice_id ?? null;
  return practiceIdIn(fromCase.data) ?? practiceIdIn(fromBusiness.data);
}

export function useCaseScopedPractice(): PracticeMembership | null {
  const practiceId = useCasePracticeId();
  const { memberships } = useActivePractice();
  return practiceId === null ? null : memberships.find((m) => m.practiceId === practiceId) ?? null;
}

export function useScopedCapability(capability: string): boolean {
  return useScopedPractice().membership?.capabilities.includes(capability) ?? false;
}

/**
 * Does the actor's membership in the case's own practice hold `capability`? The one
 * resolver for the §8.2 business audience — `useCaseScopedPractice` already reads
 * whichever of `['case']`/`['case-business']` resolved. `use-nav-context.ts` and
 * `CaseDetailScreen.tsx` used to write this separately; it has since been unified.
 */
export function useCaseScopedCapability(capability: string): boolean {
  return useCaseScopedPractice()?.capabilities.includes(capability) ?? false;
}

export function useActivePractice(): ActivePractice {
  const ctx = useContext(ActivePracticeContext);
  // Throwing rather than returning an empty value: a screen rendered outside the
  // provider would otherwise act on "no practice" silently, which is the class of
  // invisible wrong-practice behaviour this module exists to end.
  if (ctx === null) {
    throw new Error('useActivePractice must be used inside <ActivePracticeProvider>');
  }
  return ctx;
}
